import { useCallback } from 'react';
import { useMutation } from '@apollo/client';
// Api
import {
  DECLARE_TICKET_EVENT_MATCHUP_WINNER,
  SET_TICKET_EVENT_MATCHUPS,
} from 'api/ticketing/mutations';
// Types
import {
  DeclareTicketEventMatchupWinner,
  DeclareTicketEventMatchupWinnerVariables,
} from 'api/ticketing/types/DeclareTicketEventMatchupWinner';
import {
  SetTicketEventMatchups,
  SetTicketEventMatchupsVariables,
} from 'api/ticketing/types/SetTicketEventMatchups';
import { MatchupCorner } from 'api/graphql-global-types';
// Helpers
import { runTicketingChange } from 'pages/Events/runTicketingChange';
import {
  BoutFormValues,
  mapBoutsToMatchupsInput,
} from 'pages/Events/matchcard';

export type MatchcardActions = {
  /** Replaces the whole card; resolves to whether it was saved. */
  saveMatchcard: (bouts: BoutFormValues[]) => Promise<boolean>;
  /** Null clears the winner. */
  declareWinner: (
    matchupId: string,
    winnerCorner: MatchupCorner | null
  ) => Promise<boolean>;
  saving: boolean;
};

export const useMatchcardActions = (
  ticketEventId: string
): MatchcardActions => {
  const [setMatchups, { loading: saving }] = useMutation<
    SetTicketEventMatchups,
    SetTicketEventMatchupsVariables
  >(SET_TICKET_EVENT_MATCHUPS);
  const [declareMutation] = useMutation<
    DeclareTicketEventMatchupWinner,
    DeclareTicketEventMatchupWinnerVariables
  >(DECLARE_TICKET_EVENT_MATCHUP_WINNER);

  const saveMatchcard = useCallback(
    (bouts: BoutFormValues[]): Promise<boolean> =>
      runTicketingChange(
        () =>
          setMatchups({
            variables: {
              input: {
                ticketEventId,
                matchups: mapBoutsToMatchupsInput(bouts),
              },
            },
          }),
        'The matchcard is saved',
        'Could not save the matchcard'
      ),
    [setMatchups, ticketEventId]
  );

  // The returned bout updates the cached card, so a winner needs no refetch
  const declareWinner = useCallback(
    (matchupId: string, winnerCorner: MatchupCorner | null): Promise<boolean> =>
      runTicketingChange(
        () =>
          declareMutation({
            variables: { input: { ticketEventId, matchupId, winnerCorner } },
          }),
        winnerCorner ? 'Winner saved' : 'Winner cleared',
        'Could not save the winner'
      ),
    [declareMutation, ticketEventId]
  );

  return { saveMatchcard, declareWinner, saving };
};
